import { useState } from 'react'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'

interface Props {
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  /**
   * When provided, a "Rows" page-size selector is shown. Omit to render the
   * pager without a size selector (backward-compatible for existing callers).
   */
  onPageSizeChange?: (size: number) => void
  pageSizeOptions?: number[]
}

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [20, 50, 100],
}: Props) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  // Nothing to navigate and no size control to offer → render nothing (keeps the
  // old behaviour for callers that don't wire a size selector).
  if (totalPages <= 1 && !onPageSizeChange) return null

  const start = total === 0 ? 0 : (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, total)

  // Local text state for the jump-to-page input, so typing is unconstrained; the
  // value is clamped to 1..totalPages and committed on Enter or blur.
  const [jump, setJump] = useState('')
  function commitJump() {
    const n = Number.parseInt(jump, 10)
    setJump('')
    if (Number.isNaN(n)) return
    const clamped = Math.min(totalPages, Math.max(1, n))
    if (clamped !== page) onPageChange(clamped)
  }

  // Page numbers with ellipsis: always show first + last, and a window around the
  // current page.
  function pages(): (number | '...')[] {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
    const items: (number | '...')[] = [1]
    if (page > 3) items.push('...')
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
      items.push(i)
    }
    if (page < totalPages - 2) items.push('...')
    items.push(totalPages)
    return items
  }

  const arrowBtn =
    'flex h-10 w-10 items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed'

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-gray-100">
      {/* Left: range + optional page-size selector */}
      <div className="flex items-center gap-3 text-xs text-gray-500">
        <span>
          {start}–{end} of {total}
        </span>
        {onPageSizeChange && (
          <label className="flex items-center gap-1.5">
            <span>Rows:</span>
            <select
              value={pageSize}
              onChange={e => onPageSizeChange(Number(e.target.value))}
              className="border border-gray-200 rounded-lg px-1.5 py-1 text-xs text-gray-700 focus:border-orange-400 focus:outline-none"
            >
              {pageSizeOptions.map(o => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {/* Right: page-of-N, jump navigation, page numbers */}
      <div className="flex flex-wrap items-center gap-1">
        <span className="mr-1 text-xs font-medium text-gray-500 whitespace-nowrap">
          Page {page} of {totalPages}
        </span>

        <button aria-label="First page" disabled={page <= 1} onClick={() => onPageChange(1)} className={arrowBtn}>
          <ChevronsLeft className="h-4 w-4" />
        </button>
        <button aria-label="Previous page" disabled={page <= 1} onClick={() => onPageChange(page - 1)} className={arrowBtn}>
          <ChevronLeft className="h-4 w-4" />
        </button>

        {pages().map((p, i) =>
          p === '...' ? (
            <span key={`ellipsis-${i}`} className="px-1 text-gray-400 text-sm">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p as number)}
              className={`h-10 min-w-10 px-2 rounded-lg text-sm font-medium transition-colors ${
                p === page ? 'bg-brand-500 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {p}
            </button>
          )
        )}

        <button aria-label="Next page" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} className={arrowBtn}>
          <ChevronRight className="h-4 w-4" />
        </button>
        <button aria-label="Last page" disabled={page >= totalPages} onClick={() => onPageChange(totalPages)} className={arrowBtn}>
          <ChevronsRight className="h-4 w-4" />
        </button>

        {/* Jump to page */}
        <div className="ml-1 flex items-center gap-1 text-xs text-gray-500">
          <span className="hidden sm:inline">Go to</span>
          <input
            type="number"
            min={1}
            max={totalPages}
            value={jump}
            onChange={e => setJump(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') commitJump()
            }}
            onBlur={commitJump}
            placeholder={String(page)}
            aria-label="Jump to page"
            className="w-14 rounded-lg border border-gray-200 px-2 py-1.5 text-center text-xs tabular-nums focus:border-orange-400 focus:outline-none"
          />
        </div>
      </div>
    </div>
  )
}
